import React from "react";
import { motion } from "framer-motion";
import { Server, Database, Workflow, Users, Lock, Cpu, Zap } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { archNodes, archEdges } from "../data/mock";

const nodeIcon = {
  client: Users,
  service: Server,
  broker: Workflow,
  db: Database
};

const nodeAccent = {
  client: "from-zinc-500/20 border-zinc-400/30 text-zinc-200",
  service: "from-cyan-500/20 border-cyan-400/40 text-cyan-200",
  broker: "from-amber-500/20 border-amber-400/40 text-amber-200",
  db: "from-emerald-500/20 border-emerald-400/40 text-emerald-200"
};

const Architecture = () => {
  // build node map for edge lookups
  const nodeMap = Object.fromEntries(archNodes.map((n) => [n.id, n]));

  return (
    <section id="system" className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="System Design"
          title="How I think about backends."
          description="A sample service topology — gateway routing, async event bus, and isolated data stores."
        />

        <div className="relative rounded-3xl glass-strong overflow-hidden">
          {/* Header chrome */}
          <div className="flex items-center gap-2 h-10 px-4 border-b border-white/5 bg-[#0a0b10]">
            <Workflow className="w-3.5 h-3.5 text-cyan-300" />
            <span className="font-mono text-[11px] text-zinc-400">architecture.yaml</span>
            <span className="ml-auto font-mono text-[10px] text-emerald-300">● healthy</span>
          </div>

          {/* Diagram */}
          <div className="relative aspect-[16/10] md:aspect-[16/7] w-full bg-[#070810]">
            {/* grid bg */}
            <div
              className="absolute inset-0 opacity-[0.25]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(34,211,238,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.12) 1px, transparent 1px)",
                backgroundSize: "32px 32px"
              }}
            />

            {/* SVG edges with flowing packets */}
            <svg
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              <defs>
                <linearGradient id="edgeGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(34,211,238,0.1)" />
                  <stop offset="50%" stopColor="rgba(34,211,238,0.7)" />
                  <stop offset="100%" stopColor="rgba(34,211,238,0.1)" />
                </linearGradient>
              </defs>
              {archEdges.map((e, i) => {
                const from = nodeMap[e.from];
                const to = nodeMap[e.to];
                if (!from || !to) return null;
                const pathId = `edge-${i}`;
                return (
                  <g key={i}>
                    <path
                      id={pathId}
                      d={`M ${from.x} ${from.y} L ${to.x} ${to.y}`}
                      stroke="rgba(255,255,255,0.08)"
                      strokeWidth="0.25"
                      fill="none"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Animated packet */}
                    <circle r="0.7" fill="#22d3ee">
                      <animateMotion
                        dur={`${2.2 + i * 0.3}s`}
                        repeatCount="indefinite"
                        begin={`${i * 0.25}s`}
                      >
                        <mpath href={`#${pathId}`} />
                      </animateMotion>
                    </circle>
                  </g>
                );
              })}
            </svg>

            {/* Nodes */}
            {archNodes.map((n, i) => {
              const Icon = nodeIcon[n.type] || Server;
              return (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, delay: 0.05 * i }}
                  style={{
                    left: `${n.x}%`,
                    top: `${n.y}%`,
                    transform: "translate(-50%, -50%)"
                  }}
                  className="absolute"
                >
                  <div
                    className={`relative bg-gradient-to-br ${nodeAccent[n.type]} bg-[#0a0b10]/80 backdrop-blur-sm border rounded-xl px-3 py-2 min-w-[120px] text-center`}
                  >
                    <Icon className="w-3.5 h-3.5 mx-auto mb-1 opacity-80" />
                    <p className="font-mono text-[11px] font-semibold leading-none">
                      {n.label}
                    </p>
                    <p className="font-mono text-[9px] text-zinc-500 mt-1 leading-none">
                      {n.sub}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Footer legend */}
          <div className="flex flex-wrap items-center gap-4 px-5 py-3 border-t border-white/5 bg-[#06070c]">
            <LegendDot color="bg-cyan-400" label="Service" />
            <LegendDot color="bg-amber-400" label="Message Broker" />
            <LegendDot color="bg-emerald-400" label="Data Store" />
            <LegendDot color="bg-zinc-400" label="Client" />
            <span className="ml-auto font-mono text-[10px] text-zinc-500">
              packets flow L → R · async via Kafka
            </span>
          </div>
        </div>

        {/* Principles */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <Principle
            icon={Zap}
            title="Async first"
            desc="Kafka decouples producers from consumers — hot paths stay fast, slow paths retry cleanly."
            accent="amber"
          />
          <Principle
            icon={Lock}
            title="Fail isolated"
            desc="Each service owns its DB. No shared tables, no cross-service joins, no nasty surprises."
            accent="cyan"
          />
          <Principle
            icon={Cpu}
            title="Observable"
            desc="Structured logs, metrics and traces by default — if I can't see it, I can't fix it at 2 AM."
            accent="emerald"
          />
        </div>
      </div>
    </section>
  );
};

const LegendDot = ({ color, label }) => (
  <div className="inline-flex items-center gap-1.5">
    <span className={`w-2 h-2 rounded-full ${color}`} />
    <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
  </div>
);

const Principle = ({ icon: Icon, title, desc, accent }) => {
  const cls = {
    cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-400/20",
    amber: "text-amber-300 bg-amber-500/10 border-amber-400/20",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-400/20"
  }[accent];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      className="p-5 rounded-2xl glass"
    >
      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-3 ${cls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <h4 className="font-display text-base font-semibold text-white mb-1.5">{title}</h4>
      <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </motion.div>
  );
};

export default Architecture;
